import { Utils } from "@app/_helpers";
import { EXPIRY_OFFSET_HFT_DATE_FORMAT, MAXIMUM_MULTIPLIER, MAX_DEPTH_NULL_VALUE, MINIMUM_MULTIPLIER, MOMENT_COMPLETE_TIME, MOMENT_OPTION_FORMAT, PORTFOLIO_VISIBILIITY_STATUS, STRATEGY_CREATION_METHOD } from "@app/_helpers/constants";
import { RiskoMeterEnum } from "@app/_shared/shared.enums";
import { ExpiryType, LegExitTypes, MtmCheckType, ReOrderConditionTypes, StrikeSelectionType, WebApiStrategyType } from '../../../../utProto/webApiStrategyEnums_pb';
import { OrderMode, ProductType, SecurityType } from '../../../../utProto/enums_pb';
import { EXECUTION_STYLE_NUM, MAX_DEPTH_TYPE } from "../strategy-form/strategy.enums";
import { OPEN_INTEREST_LEGS_OPTIONS } from "./components/strategy-leg-form/strategy-leg-form.constants";
import { STRATEGIES_FIELD } from "./portfolio-form.constants";
import { PortfolioCreationType } from "./portfolio-form.enums";
import {
    ExitDetails, GlobalExitParameters, GlobalExitParamsFormValue,
    ICondition,
    LegDetail, LegEntryDetails, PortfolioDetails, PortfolioDisplayResponse,
    PortfolioStrategy, ReOrderParams, StrategyDetails,
    executionDaysParams
} from "./portfolio.interface";
import { LegFormComponent } from "@app/modules/trading/modules/pay-off-curve/modules/rapid/components/leg-form/leg-form.component";

export class PortfolioModel implements PortfolioDisplayResponse {
    creator = '';
    market = 0;
    algoFileName: string = '';
    id = '';
    portfolioName = '';
    minimumMultiplier = MINIMUM_MULTIPLIER;
    maximumMultiplier = MAXIMUM_MULTIPLIER;
    tags = [];
    creationMethod = STRATEGY_CREATION_METHOD.PORTFOLIO;
    strategies: StrategyModal[] = [];
    portfolioExitParam: ExitDetails = null;
    startTime = 0;
    squareOffTime = 0;
    endTime = 0;
    createdAt = 0;
    updatedAt = 0;
    subscribers = [];
    bookmarkers = [];
    isPublic = false;
    minimumCapital = 0;
    minimumCapitalOnExpiry = 0;
    minCash = 0;
    minCashOnExpiry = 0;
    uniquePortfolioID?: string;
    deployed?: boolean;
    soExtraParams? = {};
    visibilityStatus? = PORTFOLIO_VISIBILIITY_STATUS.Active;
    version = 0;
    riskOMeter = RiskoMeterEnum.Moderate;
    portfolioCreationType? = 0;
    rememberSettings: boolean = false;
    executionDaysParams: executionDaysParams;
    shareWithClients?: { [key: string]: number };
    portfolioSharedOn?: number;
    overallExchanges: number[];
    originalPortfolioId?: string;

    autoDeployPortfolio: boolean = false;
    autoDeploymentDays?: number[];
    is_custom_exit_params_for_orignal_portfolio?: boolean;

    aiAssistantThreadID?: string;
    ymlData?: string;
    isReduceMultiplierAllowed: boolean = false;

    conditions?: ICondition[]

    constructor(portfolioData: any, uniquePortfolioID?: string) {
        if (!portfolioData) { return; }
        Object.keys(this).forEach(key => {
            // making sure that we create new model instead of directly assigning the strategies array 
            if (key === STRATEGIES_FIELD) {
                this.strategies = portfolioData?.strategies?.map((strategy) => {
                    if(strategy?.createdAt?.$date) {
                        strategy.createdAt = strategy.createdAt.$date;
                    }
                    if(strategy?.portfolioSharedOn?.$date) {
                        strategy.portfolioSharedOn = strategy.portfolioSharedOn.$date;
                    }
                    return new StrategyModal(strategy)
                });
            } else {
                this[key] = portfolioData[key];
            }
        });


        if (portfolioData.tags?.length && typeof portfolioData.tags == 'string') {
            this.tags = portfolioData.tags.split(',');
        } else {
            this.tags = portfolioData.tags || [];
        }

        if (typeof portfolioData.startTime !== 'number') {
            this.startTime = +Utils.dateToTimeConvert(portfolioData.startTime);
            this.squareOffTime = +Utils.dateToTimeConvert(portfolioData.squareOffTime);
            this.endTime = +Utils.dateToTimeConvert(portfolioData.endTime);
        }

        if (this.isPublic) {
            this.isReduceMultiplierAllowed = portfolioData.isReduceMultiplierAllowed;
        }

        this.uniquePortfolioID = uniquePortfolioID;
        try {
            this.id = portfolioData?._id || portfolioData?.id;
        } catch (_) {
            console.log('No portfolio Id found in object with uniquePortfolioID: ', uniquePortfolioID);
        }

        this.riskOMeter = +this.riskOMeter;
        this.executionDaysParams = {
            executionDaysType: parseInt(portfolioData.daysToExpiryType || portfolioData.executionDaysParams?.executionDaysType),
            operatorType: parseInt(portfolioData.daysToExpiryComparator || portfolioData.executionDaysParams?.operatorType),
            daysToExpiry: portfolioData.daysToExpiryCount === 0 || portfolioData.executionDaysParams?.daysToExpiry === 0 ? 0 : portfolioData.daysToExpiryCount || portfolioData.executionDaysParams?.daysToExpiry,
            executeDays: (portfolioData.executeDays || portfolioData.executionDaysParams?.executeDays || []).map(d => +d)
        }
        this.shareWithClients = portfolioData.shareWithClients;
        if (portfolioData.autoDeployPortfolio) {
            this.autoDeploymentDays = portfolioData.autoDeploymentDays.map(d => +d);
        }

        if (portfolioData.portfolioSharedOn) {
            this.portfolioSharedOn = portfolioData.portfolioSharedOn * 1000;
        }
    }

    get isExternalStrategy() {
        return (this.creationMethod === STRATEGY_CREATION_METHOD.CPP_TYPE) || (this.creationMethod === STRATEGY_CREATION_METHOD.REST_TYPE);
    }

    get isPortfolio() {
        return (this.creationMethod === STRATEGY_CREATION_METHOD.PORTFOLIO)
            || (this.creationMethod === STRATEGY_CREATION_METHOD.YML_PORTFOLIO_CREATION);
    }
    get isTvPortfolio() {
        return (this.creationMethod === STRATEGY_CREATION_METHOD.TVPORTFOLIO)
            || this.creationMethod === STRATEGY_CREATION_METHOD.TV_YML_PORTFOLIO_CREATION;
    }

    get isCopyPortfolio() {
        return this.portfolioCreationType === PortfolioCreationType.Copy;
    }

    get isSharedPortfolio() {
        return this.portfolioCreationType === PortfolioCreationType.Shared;
    }

    setExitParams(strategyExitForm) {
        this.portfolioExitParam = {
            tpEnabled: strategyExitForm.TPEnabled ? 1 : 0,
            slEnabled: strategyExitForm.SLEnabled ? 1 : 0,
            stopLossType: strategyExitForm.SLEnabled ? parseInt(strategyExitForm.stopLossType) : undefined,
            stopLoss: strategyExitForm.SLEnabled ? Math.round(strategyExitForm.stopLossValue * 100) : undefined,
            targetProfitType: strategyExitForm.TPEnabled ? parseInt(strategyExitForm.targetProfitType) : undefined,
            targetProfit: strategyExitForm.TPEnabled ? Math.round(strategyExitForm.targetProfitValue * 100) : undefined,
            trailingSLType: strategyExitForm.trailSLEnabled ? parseInt(strategyExitForm.trailSLType) : undefined,
            trailingSL: strategyExitForm.trailSLEnabled ? Math.round(strategyExitForm.trailSLValue * 100) : undefined,
            increaseBy: strategyExitForm.trailSLEnabled ? Math.round(strategyExitForm.increaseBy * 100) : undefined,
            slToCostType: strategyExitForm.costTypeEnabled ? parseInt(strategyExitForm.costType) : undefined,
            slToCost: strategyExitForm.costTypeEnabled ? Math.round(strategyExitForm.costValue * 100) : undefined,
            underlyingType: strategyExitForm.underlyingType ? parseInt(strategyExitForm.underlyingType) : undefined,
            underlyingMinValue: strategyExitForm.underlyingMinValue ? Math.round(strategyExitForm.underlyingMinValue * 100) : undefined,
            underlyingMaxValue: strategyExitForm.underlyingMaxValue ? Math.round(strategyExitForm.underlyingMaxValue * 100) : undefined,
            trailLockedProfit: strategyExitForm.trailLockedProfit ? strategyExitForm.trailLockedProfit : undefined,
            isTrailLockedProfit: !!strategyExitForm.isTrailLockedProfit,
            trailLockedProfitType: strategyExitForm.trailLockedProfitType ? parseInt(strategyExitForm.trailLockedProfitType) : undefined,
            trailLockedProfitValue: strategyExitForm.trailLockedProfitValue ? Math.round(strategyExitForm.trailLockedProfitValue * 100) : undefined,
            lockProfitAt: strategyExitForm.lockProfitAt ? Math.round(strategyExitForm.lockProfitAt * 100) : undefined,
            onIncreaseOf: strategyExitForm.onIncreaseOf ? Math.round(strategyExitForm.onIncreaseOf * 100) : undefined,
            trailBy: strategyExitForm.trailBy ? Math.round(strategyExitForm.trailBy * 100) : undefined,
            isUnderlying: !!strategyExitForm.isUnderlying
        }
    }

    getExitForm() {
        return {
            TPEnabled: this.portfolioExitParam.targetProfit ?? false,
            targetProfitType: this.portfolioExitParam.targetProfitType?.toString(),
            targetProfitValue: (this.portfolioExitParam.targetProfit ?? null) / 100,
            SLEnabled: this.portfolioExitParam.stopLoss ?? false,
            stopLossType: this.portfolioExitParam.stopLossType?.toString(),
            stopLossValue: (this.portfolioExitParam.stopLoss ?? null) / 100,
            trailSLEnabled: this.portfolioExitParam.trailingSL ?? false,
            trailSLType: this.portfolioExitParam.trailingSLType?.toString(),
            trailSLValue: (this.portfolioExitParam.trailingSL ?? null) / 100,
            increaseBy: (this.portfolioExitParam.increaseBy ?? null) / 100,
            costTypeEnabled: this.portfolioExitParam.slToCost ?? false,
            costType: this.portfolioExitParam.slToCostType?.toString(),
            costValue: (this.portfolioExitParam.slToCost ?? null) / 100,
            underlyingType: this.portfolioExitParam.underlyingType?.toString(),
            underlyingMinValue: this.portfolioExitParam.underlyingMinValue ? this.portfolioExitParam.underlyingMinValue / 100 : null,
            underlyingMaxValue: this.portfolioExitParam.underlyingMaxValue ? this.portfolioExitParam.underlyingMaxValue / 100 : null,
            trailLockedProfit: !!this.portfolioExitParam.trailLockedProfit,
            isTrailLockedProfit: !!this.portfolioExitParam.isTrailLockedProfit,
            trailLockedProfitType: this.portfolioExitParam.trailLockedProfitType?.toString(),
            trailLockedProfitValue: (this.portfolioExitParam.trailLockedProfitValue ?? null) / 100,
            lockProfitAt: (this.portfolioExitParam.lockProfitAt ?? null) / 100,
            onIncreaseOf: (this.portfolioExitParam.onIncreaseOf ?? null) / 100,
            trailBy: (this.portfolioExitParam.trailBy ?? null) / 100,
            isUnderlying: !!this.portfolioExitParam.isUnderlying
        }
    }
}

export class StrategyModal implements PortfolioStrategy {
    strategyName = '';
    market = 0;
    StrategyType = null;
    algoType = null;
    lotMultiplier = 1;
    StrategyParams = null;
    strategySymbol = null;
    dateAdded: number = 0;
    startTime = 0;
    squareOffTime = 0;
    endTime = 0;
    executeDays = []
    SoName?: string;
    stepSize = 0;
    createdAt = null;
    uniqueStrategyId = null;
    isChecked = false;
    isPositionAvailable = false;
    strategyDetails: any;
    AlgoName? = '';
    underlying = 0;
    productType: number = ProductType.PRODUCTTYPE_INTRADAY;
    exitBeforeExpiry = 0;
    squareOffType = 0;
    slToCostToggle = 0;
    slToCostType = 0;
    totalCopyCount = 0;
    executionDaysParams: executionDaysParams;
    orderExecutionType = EXECUTION_STYLE_NUM.FULLY_AUTOMATED;
    daysToExpiryType = 0;
    daysToExpiryComparator = 0;
    daysToExpiryCount = 0;

    constructor(strategyData: PortfolioStrategy, strategyDetails?: StrategyDetails) {
        if (!strategyData) { return; }
        Object.keys(strategyData).forEach(key => {
            this[key] = strategyData[key];
        });
        if (strategyDetails) {
            this.strategyDetails = strategyDetails

        }

        if (strategyData.AlgoName) {
            strategyData.strategyName = strategyData.AlgoName;
        }
    }

    setExpiryOffset(expiryType, expiryOffset) {
        let expiryDateFormat = expiryOffset;
        if (expiryType == ExpiryType.EXPIRYTYPE_BASIC.toString()) {
            expiryDateFormat = (Utils.convertDateFormat(expiryOffset, MOMENT_OPTION_FORMAT, EXPIRY_OFFSET_HFT_DATE_FORMAT));
        }
        return expiryDateFormat;
    }


    parseStrategyForm(strategyDetails: any, legs: any[], strategyExitParams: any, strategyExitEnabled = true, confTime, exchangeCMSelected: boolean) {
        let expiryDateFormat = this.setExpiryOffset(strategyDetails.expiryType, strategyDetails.expiryOffset);
        this.strategyName = strategyDetails.strategyName;
        this.StrategyType = WebApiStrategyType.WEBAPISTRATEGYTYPE_GENERIC_ALGO;
        this.algoType = strategyDetails.strategyType;
        this.startTime = +Utils.dateToTimeConvert(strategyDetails.startTime);
        this.squareOffTime = strategyDetails.squareOffTime ? +Utils.dateToTimeConvert(strategyDetails.squareOffTime) : +Utils.dateToTimeConvert(Utils.getCurrentDate(confTime.end_time, MOMENT_COMPLETE_TIME));
        this.endTime = strategyDetails.endTime ? +Utils.dateToTimeConvert(strategyDetails.endTime)
            : +Utils.dateToTimeConvert(Utils.getCurrentDate(confTime.carry_forward_end_time, MOMENT_COMPLETE_TIME));
        this.executionDaysParams = {
            executionDaysType: parseInt(strategyDetails.daysToExpiryType),
            operatorType: parseInt(strategyDetails.daysToExpiryComparator),
            daysToExpiry: strategyDetails.daysToExpiryCount,
            executeDays: strategyDetails?.executeDays?.map(d => +d)
        }
        this.squareOffType = parseInt(strategyDetails.squareOff);
        this.underlying = parseInt(strategyDetails.underlying);
        this.productType = parseInt(strategyDetails.productType);
        this.exitBeforeExpiry = parseInt(strategyDetails.exitBeforeExpiry);
        this.slToCostToggle = strategyDetails.slToCostType ? 1 : 0;
        if (this.slToCostToggle) {
            this.slToCostType = parseInt(strategyDetails.slToCost);
        }
        this.strategySymbol = {
            exchange: +strategyDetails.exchange,
            symbol: strategyDetails.symbol,
            expiryType: parseInt(strategyDetails.expiryType),
            expiryOffset: parseInt(expiryDateFormat),
            strikeSelection: parseInt(strategyDetails.strikeSelection),
            instrumentType: SecurityType.SECURITYTYPE_OPTION,
            expiryDate: expiryDateFormat.toString(),
            series: strategyDetails.series
        }
        this.lotMultiplier = strategyDetails.lotMultiplier;
        this.dateAdded = (new Date()).getTime();

        const legArray: LegDetail[] = [];
        legs.forEach((leg) => {
            legArray.push(this.parseStrategyLegs(leg, exchangeCMSelected));
        });

        this.StrategyParams = {
            legs: legArray
        };

        if (strategyExitEnabled) {
            this.StrategyParams.strategyExitParams = {
                entryPriceMethod: parseInt(strategyExitParams.entryPriceMethod),
                slEnabled: strategyExitParams.SLEnabled ? 1 : 0,
                stopLossType: (strategyExitParams.SLEnabled) ? parseInt(strategyExitParams.stopLossType) : undefined,
                ...(strategyExitParams.SLEnabled && strategyExitParams.stopLossType !== MtmCheckType.MTMCHECKTYPE_UNDERLYING.toString() &&
                    { stopLoss: Math.round(strategyExitParams.stopLossValue * 100) }),
                ... (strategyExitParams.stopLossMin && { stopLossMin: Math.round(strategyExitParams.stopLossMin * 100) }),
                ... (strategyExitParams.stopLossMax && { stopLossMax: Math.round(strategyExitParams.stopLossMax * 100) }),
                tpEnabled: strategyExitParams.TPEnabled ? 1 : 0,
                targetProfitType: (strategyExitParams.TPEnabled) ? parseInt(strategyExitParams.targetProfitType) : undefined,
                ...(strategyExitParams.TPEnabled && strategyExitParams.targetProfitType !== MtmCheckType.MTMCHECKTYPE_UNDERLYING.toString() &&
                    { targetProfit: Math.round(strategyExitParams.targetProfitValue * 100) }),
                ... (strategyExitParams.targetProfitMin && { targetProfitMin: Math.round(strategyExitParams.targetProfitMin * 100) }),
                ... (strategyExitParams.targetProfitMax && { targetProfitMax: Math.round(strategyExitParams.targetProfitMax * 100) }),
                trailingSLType: (strategyExitParams.trailSLEnabled) ? parseInt(strategyExitParams.trailSLType) : undefined,
                trailingSL: (strategyExitParams.trailSLEnabled) ? Math.round(strategyExitParams.trailSLValue * 100) : undefined,
                increaseBy: (strategyExitParams.trailSLEnabled) ? Math.round(strategyExitParams.increaseBy * 100) : undefined,
                slToCostType: (strategyExitParams.costTypeEnabled) ? parseInt(strategyExitParams.costType) : undefined,
                slToCost: (strategyExitParams.costTypeEnabled) ? Math.round(strategyExitParams.costValue * 100) : undefined,
                ...this.parseReOrderParams(strategyExitParams),
                ...(strategyExitParams.exitIndicatorsToggle && { exitIndicatorsParams: strategyExitParams.exitIndicatorsParams }),

                trailLockedProfit: strategyExitParams.trailLockedProfit ? strategyExitParams.trailLockedProfit : undefined,
                isTrailLockedProfit: !!strategyExitParams.isTrailLockedProfit,
                trailLockedProfitType: strategyExitParams.trailLockedProfitType ? parseInt(strategyExitParams.trailLockedProfitType) : undefined,
                trailLockedProfitValue: strategyExitParams.trailLockedProfitValue ? Math.round(strategyExitParams.trailLockedProfitValue * 100) : undefined,
                lockProfitAt: strategyExitParams.lockProfitAt ? Math.round(strategyExitParams.lockProfitAt * 100) : undefined,
                onIncreaseOf: strategyExitParams.onIncreaseOf ? Math.round(strategyExitParams.onIncreaseOf * 100) : undefined,
                trailBy: strategyExitParams.trailBy ? Math.round(strategyExitParams.trailBy * 100) : undefined,

                trigger: {
                    triggerEnabled: strategyExitParams.triggerEnabled,
                    triggerType: parseInt(strategyExitParams.triggerType) || 0,
                    triggerName: strategyExitParams.triggerName || ""
                }
            }
        }

    }

    parseStrategyLegs(legForm, exchangeCMSelected): any {
        let expiryDateFormat = this.setExpiryOffset(legForm.expiryType, legForm.expiryOffset);

        let strikeVal = 0;
        // For Equity
        if (exchangeCMSelected) {
            strikeVal = parseInt(legForm.premium);
            legForm.instrumentType = '0';
        }
        // For Strike - ATM 
        else if (this.strategySymbol.strikeSelection == 0) {
            const split = legForm.strikePriceValue.trim().split(/\s+/);
            if (split.length > 1) {
                strikeVal = parseFloat(split[2]) * 100;
                if (split[1] == '-') {
                    strikeVal = -Math.abs(strikeVal);
                }
            }
        }
        // For Strike - Basic
        else if (this.strategySymbol.strikeSelection == 1) {
            strikeVal = Math.round(legForm.strikePriceValue * 100);
        }
        //For Open Interest
        else if (this.strategySymbol.strikeSelection === StrikeSelectionType.STRIKESELECTIONTYPE_OI) {
            strikeVal = OPEN_INTEREST_LEGS_OPTIONS[legForm.strikePriceValue];
        }
        //For Price
        else {
            const value = parseInt(legForm.priceCriteria);
            if (value === StrikeSelectionType.STRIKESELECTIONTYPE_PRICE_ABOVE || value === StrikeSelectionType.STRIKESELECTIONTYPE_PRICE_BELOW || value === StrikeSelectionType.STRIKESELECTIONTYPE_PRICE_NEAREST) {
                strikeVal = Math.round(legForm.premium * 100);
            } else if (value === StrikeSelectionType.STRIKESELECTIONTYPE_PRICE_RANGE) {
                if (parseInt(legForm.orderMode) === OrderMode.ORDERMODE_BUY) {
                    strikeVal = Math.round(legForm.minPriceRange * 100);
                } else {
                    strikeVal = Math.round(legForm.maxPriceRange * 100);
                }
            } else if (value === StrikeSelectionType.STRIKESELECTIONTYPE_SPOT_PERCENTAGE || value === StrikeSelectionType.STRIKESELECTIONTYPE_STRADDLE_WIDTH) {
                strikeVal = (legForm.percentOfAtmValue) * 100;
            } else if (value === StrikeSelectionType.STRIKESELECTIONTYPE_STRADDLE_PERCENT) {
                strikeVal = Math.round(legForm.straddlePrice) * 100;
            }
        }

        let legDetails: LegDetail = {
            orderMode: parseInt(legForm.orderMode),
            orderExecutionType: parseInt(legForm.orderExecutionType) || EXECUTION_STYLE_NUM.FULLY_AUTOMATED,
            ...(legForm.legName && { legName: legForm.legName }),
            legSymbol: {
                price: {
                    priceValue: legForm.toggleEquityPrice ? Math.round(legForm.equityPrice * Math.pow(10, legForm.scripPrecision)) : 0,
                    isEnabled: legForm.toggleEquityPrice
                },
                exchange: parseInt(this.strategySymbol.exchange),
                symbol: (exchangeCMSelected) ? legForm.symbol : this.strategySymbol.symbol,
                instrumentType: 2,
                securityId: legForm.securityId,
                optionMode: parseInt(legForm.instrumentType),
                expiryType: parseInt(legForm.expiryType),
                expiryOffset: parseInt(expiryDateFormat),
                strikeSelection: legForm.priceCriteria ? parseInt(legForm.priceCriteria) : this.strategySymbol.strikeSelection,
                strikeValue: strikeVal,
                expiryDate: expiryDateFormat?.toString(),
                series: legForm.series,
                ...(legForm.rangePercentageEnabled && {
                    rangePercentageEnabled: legForm.rangePercentageEnabled,
                    rangePercentageValue: legForm.rangePercentageValue
                }),
                ...(parseInt(legForm.priceCriteria) === StrikeSelectionType.STRIKESELECTIONTYPE_PRICE_RANGE && {
                    minPriceRange: legForm.minPriceRange * 100,
                    maxPriceRange: legForm.maxPriceRange * 100
                }),
                ...((parseInt(legForm.priceCriteria) === StrikeSelectionType.STRIKESELECTIONTYPE_SPOT_PERCENTAGE || parseInt(legForm.priceCriteria) === StrikeSelectionType.STRIKESELECTIONTYPE_STRADDLE_WIDTH) && {
                    operatorValue: parseInt(legForm.operatorValue)
                })
            },
            maxDepthEnabled: legForm.maxDepthEnabled ? 1 : 0,
            ...(legForm.maxDepthEnabled && {
                maxDepthValue: {
                    itm: legForm.maxDepthType === MAX_DEPTH_TYPE.OTM ? MAX_DEPTH_NULL_VALUE : legForm.itmValue,
                    otm: legForm.maxDepthType === MAX_DEPTH_TYPE.ITM ? MAX_DEPTH_NULL_VALUE : legForm.otmValue
                }
            }),
            lots: legForm.lotMultiplier,
            ... (legForm.legStartTimeToggle && { legStartTime: +Utils.dateToTimeConvert(legForm.legStartTime) }),
            ... (legForm.legEndTimeToggle && { legEndTime: +Utils.dateToTimeConvert(legForm.legEndTime) }),
            legStartTimeToggle: legForm.legStartTimeToggle ? 1 : 0,
            legEndTimeToggle: legForm.legEndTimeToggle ? 1 : 0,
            legSettingToggle: legForm.entrySettingToggle || legForm.exitSettingToggle ? 1 : 0,
            entrySettingToggle: legForm.entrySettingToggle ? 1 : 0,
            exitSettingToggle: legForm.exitSettingToggle ? 1 : 0,
            ...(legForm.entrySettingToggle && {
                ...(legForm.entryIndicatorsToggle && { entryIndicatorsParams: legForm.entryIndicatorsParams }),
                waitToTradeToggle: legForm.waitToTradeToggle ? 1 : 0,
                ...(legForm.waitToTradeToggle && {
                    waitToTradeType: parseInt(legForm.waitToTradeType),
                    waitToTradeValue: Math.round(legForm.waitToTradeValue * 100),
                }),
                ...(legForm.rangeThreshold && { legEntryParams: { ...this.parseLegEntry(legForm) } }),
            }),
            
            ...(legForm.exitSettingToggle && {
                ...(legForm.exitIndicatorsToggle && { exitIndicatorsParams: legForm.exitIndicatorsParams }),
                ...((legForm.reOrderConditionForm.tpReOrderToggle || legForm.reOrderConditionForm.slReOrderToggle || legForm.exitForm.replayCountToggle) && { ...this.parseReOrderParams(legForm.reOrderConditionForm, legForm.exitForm) }),
                ...((parseInt(legForm.exitForm.underlyingType) || legForm.exitForm.exitSettingToggle || legForm.exitForm.oiTimerEnabled) && {
                    legExitParams: {
                        underlyingToggle: legForm.exitForm.underlyingToggle ? 1 : 0,
                        underlyingType: parseInt(legForm.exitForm.underlyingType),
                        netPLToggle: legForm.exitSettingToggle ? 1 : 0,
                        ...(this.strategySymbol.strikeSelection === StrikeSelectionType.STRIKESELECTIONTYPE_OI) && {
                            oiTimerEnabled: legForm.exitForm.oiTimerEnabled ? true : false,
                        },

                        // Exit form properties if underlying toggle is on.
                        ...(parseInt(legForm.exitForm.underlyingType)) && {
                            ...(legForm.exitForm.underlyingMinValue !== undefined && legForm.exitForm.underlyingMinValue !== null
                                && legForm.exitForm.underlyingMinValue !== '') && {
                                underlyingMinValue: Math.round(legForm.exitForm.underlyingMinValue * 100),
                                underlyingMinType: Utils.targetStopLossCalculator(legForm.orderMode, legForm.instrumentType, true),
                            },
                            ...(legForm.exitForm.underlyingMaxValue !== undefined && legForm.exitForm.underlyingMaxValue !== null
                                && legForm.exitForm.underlyingMaxValue !== '') && {
                                underlyingMaxValue: Math.round(legForm.exitForm.underlyingMaxValue * 100),
                                underlyingMaxType: Utils.targetStopLossCalculator(legForm.orderMode, legForm.instrumentType, false),
                            }
                        },
                        // Exit form properties if net PL toggle is on.
                        ...(legForm.exitSettingToggle) && {
                            targetProfit: (legForm.exitForm.TPEnabled) ? Math.round(legForm.exitForm.targetProfitValue * 100) : undefined,
                            targetProfitType: (legForm.exitForm.TPEnabled) ? (legForm.exitForm.targetProfitType && parseInt(legForm.exitForm.targetProfitType)) : undefined,
                            stopLossType: (legForm.exitForm.SLEnabled) ? (legForm.exitForm.stopLossType && parseInt(legForm.exitForm.stopLossType)) : undefined,
                            stopLoss: (legForm.exitForm.SLEnabled) ? Math.round(legForm.exitForm.stopLossValue * 100) : undefined,
                            trailingSLType: (legForm.exitForm.trailSLEnabled) ? (legForm.exitForm.trailSLType && parseInt(legForm.exitForm.trailSLType)) : undefined,
                            trailingSL: (legForm.exitForm.trailSLEnabled) ? Math.round(legForm.exitForm.trailSLValue * 100) : undefined,
                            increaseBy: (legForm.exitForm.trailSLEnabled) ? Math.round(legForm.exitForm.increaseBy * 100) : undefined,
                            slToCostType: legForm.exitForm.costTypeEnabled ? (legForm.exitForm.costType && parseInt(legForm.exitForm.costType)) : undefined,
                            slToCost: legForm.exitForm.costTypeEnabled ? Math.round(legForm.exitForm.costValue * 100) : undefined
                        },
                        ...(this.strategySymbol.strikeSelection && legForm.exitForm.oiTimerEnabled) && {
                            oiTimer: Math.round(legForm.exitForm.oiTimer * 100)
                        }
                    }
                }
                )
            })

        };
        if (legForm.maxDepthEnabled && legForm.maxDepthType === MAX_DEPTH_TYPE.BOTH) {
            legDetails.maxDepthValue.itm = legForm.otmValue;
            legDetails.maxDepthValue.otm = legForm.otmValue;
        }
        return legDetails;

    }

    parseLegEntry(legForm): LegEntryDetails {
        let legEntry: LegEntryDetails = {
            entryOnType: parseInt(legForm.legEntryForm.entryOnType),
            rangeThreshold: parseInt(legForm.legEntryForm.rangeThreshold),
            rangeType: parseInt(legForm.legEntryForm.rangeType),
            ...(!parseInt(legForm.legEntryForm.rangeType) && {
                startTime: +Utils.dateToTimeConvert(legForm.legEntryForm.startTime),
                endTime: +Utils.dateToTimeConvert(legForm.legEntryForm.endTime),
            }),
            ...(parseInt(legForm.legEntryForm.rangeType) && {
                rangeMinvalue: Math.round(legForm.legEntryForm.rangeMinvalue * 100),
                rangeMaxvalue: Math.round(legForm.legEntryForm.rangeMaxvalue * 100),
            })
        }

        return legEntry;
    }

    parseReOrderParams(reOrderForm, exitFormleg = null): ReOrderParams {
        // Determine which form to use for replayCountToggle based on exitFormleg availability
        const replayCountToggle = exitFormleg?.replayCountToggle !== null && exitFormleg?.replayCountToggle !== undefined 
            ? exitFormleg.replayCountToggle 
            : reOrderForm.replayCountToggle;
        
        const replayCount = exitFormleg?.replayCount !== null && exitFormleg?.replayCount !== undefined 
            ? exitFormleg.replayCount 
            : reOrderForm.replayCount;

        let legReorderParam: ReOrderParams = {
            ...(reOrderForm.tpReOrderToggle && {
                tpReOrderParams: {
                    reOrderType: parseInt(reOrderForm.tpReOrderType),
                    reOrderValue: Math.round(reOrderForm.tpReOrderValue * 100),
                    ...(reOrderForm.tpReOrderLeg && parseInt(reOrderForm.tpReOrderType) === ReOrderConditionTypes.REORDERCONDITIONTYPES_DEPENDENT && {
                        reOrderLeg: reOrderForm.tpReOrderLeg,
                    })
                }
            }),
            ...(reOrderForm.slReOrderToggle && {
                slReOrderParams: {
                    reOrderType: parseInt(reOrderForm.slReOrderType),
                    reOrderValue: Math.round(reOrderForm.slReOrderValue * 100),
                    ...(reOrderForm.slReOrderLeg && parseInt(reOrderForm.slReOrderType) === ReOrderConditionTypes.REORDERCONDITIONTYPES_DEPENDENT && { 
                        reOrderLeg: reOrderForm.slReOrderLeg 
                    })
                }
            }),
            ...(replayCountToggle && {
                replayCount: replayCount * 100
            })
        }

        return legReorderParam;
    }

    getExpiryOffset(expiryType, expiryOffset) {
        let expiryOffVal;
        if (expiryType == ExpiryType.EXPIRYTYPE_BASIC) {
            expiryOffVal = (Utils.convertDateFormat(expiryOffset, EXPIRY_OFFSET_HFT_DATE_FORMAT, MOMENT_OPTION_FORMAT)).toUpperCase();
        } else {
            expiryOffVal = expiryOffset.toString();
        }

        return expiryOffVal;
    }

    getStrategyDetailsForm() {

        let expiryOffVal = this.getExpiryOffset(this.strategySymbol.expiryType, this.strategySymbol.expiryOffset);
        return {
            strategyName: this.strategyName || this.AlgoName,
            strategyType: this.algoType?.toString(),
            exchange: (this.strategySymbol.exchange).toString(),
            symbol: this.strategySymbol.symbol,
            expiryType: this.strategySymbol.expiryType.toString(),
            expiryOffset: expiryOffVal,
            lotMultiplier: this.lotMultiplier,
            strikeSelection: this.strategySymbol.strikeSelection.toString(),
            squareOff: this.squareOffType.toString(),
            underlying: this.underlying.toString(),
            productType: this.productType.toString(),
            // exitBeforeExpiry: this.exitBeforeExpiry.toString(),
            slToCostType: this.slToCostToggle ? true : false,
            slToCost: this.slToCostType?.toString(),
            series: this.strategySymbol.series,
            startTime: Utils.timeToDateConvert(this.startTime),
            squareOffTime: Utils.timeToDateConvert(this.squareOffTime),
            endTime: Utils.timeToDateConvert(this.endTime),
            exitBeforeExpiry: this.exitBeforeExpiry,
            daysToExpiryType: this.executionDaysParams.executionDaysType?.toString(),
            daysToExpiryComparator: this.executionDaysParams.operatorType?.toString(),
            daysToExpiryCount: this.executionDaysParams.daysToExpiry,
            executeDays: this.executionDaysParams.executeDays?.map((day) => day?.toString()),
        };
    }


    getLegForm(legId: number, legIndex: number, exchangeCMSelected: boolean) {
        const legObj = legId ? this.StrategyParams.legs.find(leg => leg.legId === legId) : this.StrategyParams.legs[legIndex];
        let strikeVal;
        let strikeValInput;
        // For Strike - ATM
        if (legObj.legSymbol.strikeSelection == 0) {
            if (legObj.legSymbol.strikeValue > 0) {
                strikeVal = "ATM + " + Math.abs(legObj.legSymbol.strikeValue / 100).toString();

            }
            else if (legObj.legSymbol.strikeValue < 0) {
                strikeVal = "ATM - " + Math.abs(legObj.legSymbol.strikeValue / 100).toString();
            }
            else {
                strikeVal = "ATM";
            }

        }

        // For equity 
        else if (exchangeCMSelected) {
            strikeValInput = legObj.legSymbol.strikeValue.toString();
        }

        // For Strike - Basic
        else if (legObj.legSymbol.strikeSelection == 1) {
            strikeVal = ((legObj.legSymbol.strikeValue / 100).toFixed(2)).toString();
        }

        // For Open Interest 
        else if (legObj.legSymbol.strikeSelection === StrikeSelectionType.STRIKESELECTIONTYPE_OI) {
            strikeVal = Object.keys(OPEN_INTEREST_LEGS_OPTIONS).find(key => legObj.legSymbol.strikeValue === OPEN_INTEREST_LEGS_OPTIONS[key]);
        }

        //For Price
        else {
            strikeValInput = ((legObj.legSymbol.strikeValue / 100).toFixed(2)).toString();
        }
        let expiryOffVal = this.getExpiryOffset(legObj.legSymbol.expiryType, legObj.legSymbol.expiryOffset);

        let legDetail: any = {
            ...('orderExecutionType' in legObj && {
                orderExecutionType: legObj.orderExecutionType
            }),
            ...(legObj.legName && { legName: legObj.legName }),
            orderMode: legObj.orderMode.toString(),
            instrumentType: legObj.legSymbol.optionMode.toString(),
            optionMode: legObj.legSymbol.optionMode.toString(),
            expiryType: legObj.legSymbol.expiryType.toString(),
            expiryOffset: expiryOffVal,
            toggleEquityPrice: legObj.legSymbol.price?.isEnabled,
            equityPrice: legObj.legSymbol.price?.isEnabled ? legObj.legSymbol.price.priceValue : null,
            strikePriceValue: strikeVal,
            premium: strikeValInput ? strikeValInput : '1',
            priceCriteria: legObj.legSymbol.strikeSelection.toString(),
            rangePercentageEnabled: legObj.legSymbol.rangePercentageEnabled ?? false,
            rangePercentageValue: legObj.legSymbol.rangePercentageValue,
            symbol: legObj.legSymbol.symbol,
            market: legObj.legSymbol.exchange,
            securityId: legObj.legSymbol.securityId,
            series: legObj.legSymbol.series,
            lotMultiplier: legObj.lots,
            maxDepthEnabled: !!legObj.maxDepthEnabled,
            ...(parseInt(legObj.legSymbol.strikeSelection) === StrikeSelectionType.STRIKESELECTIONTYPE_PRICE_RANGE && {
                minPriceRange: (legObj.legSymbol.minPriceRange/100).toFixed(2),
                maxPriceRange: (legObj.legSymbol.maxPriceRange/100).toFixed(2)
            }),
            ...((parseInt(legObj.legSymbol.strikeSelection) === StrikeSelectionType.STRIKESELECTIONTYPE_SPOT_PERCENTAGE || parseInt(legObj.legSymbol.strikeSelection) === StrikeSelectionType.STRIKESELECTIONTYPE_STRADDLE_WIDTH) && {
                operatorValue: legObj.legSymbol.operatorValue.toString(),
                percentOfAtmValue: parseInt(legObj.legSymbol.strikeSelection) === StrikeSelectionType.STRIKESELECTIONTYPE_SPOT_PERCENTAGE ? Math.ceil(legObj.legSymbol.strikeValue / 100) : (legObj.legSymbol.strikeValue / 100).toFixed(2),
            }),
            ...(parseInt(legObj.legSymbol.strikeSelection) === StrikeSelectionType.STRIKESELECTIONTYPE_STRADDLE_PERCENT && {
                straddlePrice: Math.ceil(legObj.legSymbol.strikeValue / 100),
            }),
            ...(legObj.maxDepthEnabled && {
                maxDepthValue: {
                    itm: legObj.maxDepthValue.itm,
                    otm: legObj.maxDepthValue.otm
                }
            }),
            exitEnabled: legObj.legExitParams ? true : false,
            ...(legObj.legStartTimeToggle && { legStartTime: Utils.timeToDateConvert(legObj.legStartTime) }),
            ...(legObj.legEndTimeToggle && { legEndTime: Utils.timeToDateConvert(legObj.legEndTime) }),
            legStartTimeToggle: legObj.legStartTimeToggle ? true : false,
            legEndTimeToggle: legObj.legEndTimeToggle ? true : false,
            legSettingToggle: legObj.entrySettingToggle || legObj.exitSettingToggle ? true : false,
            entrySettingToggle: legObj.entrySettingToggle ? true : false,
            exitSettingToggle: legObj.exitSettingToggle ? true : false,
            waitToTradeToggle: legObj.waitToTradeToggle ? true : false,
            rangeThreshold: legObj.legEntryParams ? true : false,
            reOrderConditionToggle: (legObj.tpReOrderParams || legObj.slReOrderParams || legObj.replayCount) ? true : false,
            entryIndicatorsToggle: legObj.entryIndicatorsParams ? true : false,
            exitIndicatorsToggle: legObj.exitIndicatorsParams ? true : false,
            ...(legObj.waitToTradeToggle && {
                waitToTradeType: legObj.waitToTradeType.toString(),
                waitToTradeValue: legObj.waitToTradeValue / 100
            }),
            ...(legObj.legEntryParams && {
                legEntryForm: this.getLegEntryForm(legObj.legEntryParams)
            }),
            ...((legObj.tpReOrderParams || legObj.slReOrderParams || legObj.replayCount) && {
                reOrderConditionForm: { ...this.getLegReOrderForm(legObj.tpReOrderParams, legObj.slReOrderParams, legObj.replayCount) }
            }),
            ...(legObj.entryIndicatorsParams && {
                entryIndicatorsParams: legObj.entryIndicatorsParams
            }),
            ...(legObj.exitIndicatorsParams && {
                exitIndicatorsParams: legObj.exitIndicatorsParams
            }),
        };

        const legExitObj = legObj.legExitParams;

        if (legObj.legExitParams) {
            legDetail.exitForm = {
                TPEnabled: legExitObj.targetProfit ?? false,
                targetProfitType: legExitObj.targetProfitType?.toString(),
                targetProfitValue: legExitObj.targetProfit / 100,
                SLEnabled: legExitObj.stopLoss ?? false,
                stopLossType: legExitObj.stopLossType?.toString(),
                stopLossValue: legExitObj.stopLoss / 100,
                trailSLEnabled: legExitObj.trailingSL ?? false,
                trailSLType: (legExitObj.trailingSLType?.toString()),
                trailSLValue: legExitObj.trailingSL / 100,
                costTypeEnabled: legExitObj.slToCost ?? false,
                costType: (legExitObj.slToCostType?.toString()),
                costValue: legExitObj.slToCost / 100,
                increaseBy: legExitObj.increaseBy / 100,
                netPLToggle: legObj.exitSettingToggle ? true : false,
                oiTimerEnabled: legExitObj.oiTimerEnabled ? true : false,
                oiTimer: legExitObj.oiTimer / 100,
                replayCountToggle: legObj.replayCount ? true : false,
                ...(legObj.replayCount && {
                    replayCount: legObj.replayCount / 100,
                }),
                underlyingToggle: legExitObj.underlyingToggle ?? false,
                underlyingType: legExitObj.underlyingType?.toString(),
                ... (legExitObj.underlyingMinValue ||
                    ((legExitObj.underlyingMinValue || legExitObj.underlyingMinValue === 0)
                        && legExitObj.underlyingType === LegExitTypes.LEGEXITTYPES_DELTA))
                && { underlyingMinValue: legExitObj.underlyingMinValue / 100 },
                ... (legExitObj.underlyingMaxValue ||
                    ((legExitObj.underlyingMaxValue || legExitObj.underlyingMaxValue === 0)
                        && legExitObj.underlyingType === LegExitTypes.LEGEXITTYPES_DELTA))
                && { underlyingMaxValue: legExitObj.underlyingMaxValue / 100 },
                ... (legExitObj.underlyingMinType) && { underlyingMinType: legExitObj.underlyingMinType },
                ... (legExitObj.underlyingMaxType) && { underlyingMaxType: legExitObj.underlyingMaxType }
            }
        }
        return legDetail;
    }

    getLegEntryForm(legEntryParams) {
        return {
            entryOnType: legEntryParams.entryOnType.toString(),
            rangeThreshold: legEntryParams.rangeThreshold.toString(),
            rangeType: legEntryParams.rangeType.toString(),
            ...(!legEntryParams.rangeType && {
                startTime: Utils.timeToDateConvert(legEntryParams.startTime),
                endTime: Utils.timeToDateConvert(legEntryParams.endTime),
            }),
            ...(legEntryParams.rangeType && {
                rangeMinvalue: legEntryParams.rangeMinvalue / 100,
                rangeMaxvalue: legEntryParams.rangeMaxvalue / 100,
            })
        };
    }

    getLegReOrderForm(tpReOrderParams, slReOrderParams, replayCount?) {
        return {
            tpReOrderToggle: tpReOrderParams ? true : false,
            ...(tpReOrderParams && {
                tpReOrderType: tpReOrderParams.reOrderType.toString(),
                tpReOrderValue: (tpReOrderParams.reOrderValue || 0) / 100,
                ...(tpReOrderParams.reOrderLeg && parseInt(tpReOrderParams.reOrderType) === ReOrderConditionTypes.REORDERCONDITIONTYPES_DEPENDENT && {
                    tpReOrderLeg: tpReOrderParams.reOrderLeg
                })
            }),
            slReOrderToggle: slReOrderParams ? true : false,
            ...(slReOrderParams && {
                slReOrderType: slReOrderParams.reOrderType.toString(),
                slReOrderValue: (slReOrderParams.reOrderValue || 0) / 100,
                ...(slReOrderParams.reOrderLeg && parseInt(slReOrderParams.reOrderType) === ReOrderConditionTypes.REORDERCONDITIONTYPES_DEPENDENT && {
                    slReOrderLeg: slReOrderParams.reOrderLeg
                })
            }),
            replayCountToggle: replayCount ? true : false,
            replayCount: replayCount / 100,

        }
    }

    getStratgyExitForm() {
        return {
            entryPriceMethod: this.StrategyParams.strategyExitParams?.entryPriceMethod?.toString() ?? null,
            TPEnabled: this.StrategyParams.strategyExitParams?.tpEnabled ? true : false,
            targetProfitType: this.StrategyParams.strategyExitParams?.targetProfitType?.toString() ?? null,
            targetProfitValue: this.StrategyParams.strategyExitParams?.targetProfit / 100 ?? null,
            ... (this.StrategyParams.strategyExitParams?.targetProfitMin
                || ((this.StrategyParams.strategyExitParams?.targetProfitMin === 0 || this.StrategyParams.strategyExitParams?.targetProfitMin)
                    && this.StrategyParams.strategyExitParams?.targetProfitType === MtmCheckType.MTMCHECKTYPE_DELTA))
            && { targetProfitMin: this.StrategyParams.strategyExitParams?.targetProfitMin / 100 },
            ... (this.StrategyParams.strategyExitParams?.targetProfitMax
                || ((this.StrategyParams.strategyExitParams?.targetProfitMax === 0 || this.StrategyParams.strategyExitParams?.targetProfitMax)
                    && this.StrategyParams.strategyExitParams?.targetProfitType === MtmCheckType.MTMCHECKTYPE_DELTA))
            && { targetProfitMax: this.StrategyParams.strategyExitParams?.targetProfitMax / 100 },
            SLEnabled: this.StrategyParams.strategyExitParams?.slEnabled ? true : false,
            stopLossType: this.StrategyParams.strategyExitParams?.stopLossType?.toString() ?? null,
            stopLossValue: this.StrategyParams.strategyExitParams?.stopLoss / 100 ?? null,
            ... (this.StrategyParams.strategyExitParams?.stopLossMin) && { stopLossMin: this.StrategyParams.strategyExitParams?.stopLossMin / 100 },
            ... (this.StrategyParams.strategyExitParams?.stopLossMax) && { stopLossMax: this.StrategyParams.strategyExitParams?.stopLossMax / 100 },
            trailSLEnabled: this.StrategyParams.strategyExitParams?.trailingSL ?? false,
            trailSLType: this.StrategyParams.strategyExitParams?.trailingSLType?.toString() ?? null,
            trailSLValue: this.StrategyParams.strategyExitParams?.trailingSL / 100 ?? null,
            increaseBy: this.StrategyParams.strategyExitParams?.increaseBy / 100 ?? null,
            costTypeEnabled: this.StrategyParams.strategyExitParams?.slToCost ?? false,
            costType: this.StrategyParams.strategyExitParams?.slToCostType?.toString() ?? null,
            costValue: this.StrategyParams.strategyExitParams?.slToCost / 100 ?? null,
            tpReOrderToggle: this.StrategyParams.strategyExitParams?.tpReOrderParams ? true : false,
            slReOrderToggle: this.StrategyParams.strategyExitParams?.slReOrderParams ? true : false,
            ...this.getLegReOrderForm(this.StrategyParams.strategyExitParams?.tpReOrderParams, this.StrategyParams.strategyExitParams?.slReOrderParams),
            exitIndicatorsToggle: this.StrategyParams.strategyExitParams?.exitIndicatorsParams ? true : false,
            ...(this.StrategyParams.strategyExitParams?.exitIndicatorsParams && {
                exitIndicatorsParams: this.StrategyParams.strategyExitParams?.exitIndicatorsParams
            }),

            trailLockedProfit: !!this.StrategyParams.strategyExitParams?.trailLockedProfit,
            isTrailLockedProfit: !!this.StrategyParams.strategyExitParams?.isTrailLockedProfit,
            trailLockedProfitType: this.StrategyParams.strategyExitParams?.trailLockedProfitType?.toString(),
            trailLockedProfitValue: (this.StrategyParams.strategyExitParams?.trailLockedProfitValue ?? null) / 100,
            lockProfitAt: (this.StrategyParams.strategyExitParams?.lockProfitAt ?? null) / 100,
            onIncreaseOf: (this.StrategyParams.strategyExitParams?.onIncreaseOf ?? null) / 100,
            trailBy: (this.StrategyParams.strategyExitParams?.trailBy ?? null) / 100,

            triggerEnabled: this.StrategyParams.strategyExitParams?.trigger?.triggerEnabled,
            triggerType: this.StrategyParams.strategyExitParams?.trigger?.triggerType?.toString() ?? null,
            triggerName: this.StrategyParams.strategyExitParams?.trigger?.triggerName
        }
    }

}

// export class PortfolioExitDetailsModal implements ExitDetails {
//     valueType = '';
//     targetProfit = null;
//     stopLoss = null;
//     stopLossType = '';
//     targetProfitType = '';
//     moveSlToCost = false;
//     trailSlToggle = false;
//     startTime = null;
//     endTime = null;
//     executeOn = [];

//     constructor(exitData: ExitDetails) {
//         if (!exitData) { return; }
//         Object.keys(this).forEach(key => {
//             this[key] = exitData[key];
//         });
//     }
// }

export class PortfolioDetailsModal implements PortfolioDetails {
    minimumMultiplier = MINIMUM_MULTIPLIER;
    maximumMultiplier = MAXIMUM_MULTIPLIER;
    tags = [];
    constructor(data: PortfolioDetails) {
        if (!data) { return; }
        Object.keys(this).forEach(key => {
            this[key] = data[key];
        });
    }
}

// export class LegDetailsModal implements LegDetail {
//     orderMode = null;
//     instrumentType = null;
//     expiryType = null;
//     expiryOffset = '';
//     strike = null;
//     lots = 1;
//     legExit = false;
//     legExitDetails: ExitDetails = null;

//     constructor(legData: LegDetail) {
//         if (!legData) { return; }
//         Object.keys(this).forEach(key => {
//             this[key] = legData[key];
//         });
//     }
// }

export class GlobalExitParamsModel {
    tpEnabled: number;
    targetProfitType: number;
    targetProfit: number;
    slEnabled: number;
    stopLossType: number;
    stopLoss: number;
    trailingSLType: number;
    trailingSL: number;
    increaseBy: number;
    isTrailLockedProfit: boolean;
    trailLockedProfitValue: number;
    lockProfitAt: number;
    onIncreaseOf: number;
    trailBy: number;
    trailLockedProfit: boolean;
    trailLockedProfitType: number;
    rememberSettings: boolean;

    constructor(globalParams?: GlobalExitParameters) {

        globalParams && Object.keys(globalParams).forEach(key => {
            this[key] = globalParams[key];
        });

    }

    setGlobalParamsRequest(formVal: any) {
        this.tpEnabled = formVal.GTPEnabled ? 1 : 0,
            this.slEnabled = formVal.GSLEnabled ? 1 : 0,
            this.targetProfitType = formVal.GTPEnabled ? parseInt(formVal.globalTargetProfitType) : undefined,
            this.targetProfit = formVal.GTPEnabled ? Math.round(formVal.globalTargetProfitValue * 100) : undefined,
            this.stopLossType = formVal.GSLEnabled ? parseInt(formVal.globalStopLossType) : undefined,
            this.stopLoss = formVal.GSLEnabled ? Math.round(formVal.globalStopLossValue * 100) : undefined,
            this.trailingSLType = formVal.trailGSLEnabled ? parseInt(formVal.trailGSLType) : undefined,
            this.trailingSL = formVal.trailGSLEnabled ? Math.round(formVal.trailGSLValue * 100) : undefined,
            this.increaseBy = formVal.trailGSLEnabled ? Math.round(formVal.gslTrailBy * 100) : undefined,
            this.isTrailLockedProfit = !!formVal.isTrailLockedProfit,
            this.lockProfitAt = formVal.trailLockedProfit ? Math.round(formVal.lockProfitAt * 100) : undefined,
            this.trailLockedProfit = formVal.trailLockedProfit ? formVal.trailLockedProfit : undefined,
            this.trailLockedProfitType = formVal.trailLockedProfitType ? parseInt(formVal.trailLockedProfitType) : undefined,
            this.trailLockedProfitValue = formVal.trailLockedProfitValue ? Math.round(formVal.trailLockedProfitValue * 100) : undefined,
            this.onIncreaseOf = formVal.trailLockedProfit && !!formVal.isTrailLockedProfit ? Math.round(formVal.onIncreaseOf * 100) : undefined,
            this.trailBy = formVal.trailLockedProfit && !!formVal.isTrailLockedProfit ? Math.round(formVal.trailBy * 100) : undefined,
            this.rememberSettings = formVal.rememberSettings || false
    }

    parseGlobalParamsResponse() {
        const formVal: GlobalExitParamsFormValue = {
            GTPEnabled: !!this.tpEnabled,
            globalTargetProfitType: this.targetProfitType?.toString() || null,
            globalTargetProfitValue: this.targetProfit ? this.targetProfit / 100 : null,
            GSLEnabled: !!this.slEnabled,
            globalStopLossType: this.stopLossType?.toString() || null,
            globalStopLossValue: this.stopLoss ? this.stopLoss / 100 : null,
            trailGSLEnabled: this.trailingSLType !== undefined,
            trailGSLType: this.trailingSLType?.toString() || null,
            trailGSLValue: this.trailingSL ? this.trailingSL / 100 : null,
            gslTrailBy: this.increaseBy ? this.increaseBy / 100 : null,
            trailLockedProfit: !!this.trailLockedProfit,
            isTrailLockedProfit: !!this.isTrailLockedProfit,
            trailLockedProfitType: this.trailLockedProfitType?.toString(),
            trailLockedProfitValue: (this.trailLockedProfitValue ?? null) / 100,
            lockProfitAt: (this.lockProfitAt ?? null) / 100,
            onIncreaseOf: (this.onIncreaseOf ?? null) / 100,
            trailBy: (this.trailBy ?? null) / 100,
            rememberSettings: this.rememberSettings || false,
        }
        return formVal
    }
}